<template>
  <div v-if="graph.nodes.length" class="relationship-sankey">
    <div class="relationship-sankey__legend" aria-label="邀请关系状态图例">
      <span><i class="legend-flow legend-flow--qualified"></i>已达标</span>
      <span><i class="legend-flow legend-flow--pending"></i>待达标</span>
      <span><i class="legend-flow legend-flow--rejected"></i>已拒绝</span>
      <span><i class="legend-node"></i>点击带“展开”提示的节点继续下钻</span>
    </div>
    <div class="relationship-sankey__summary" aria-live="polite">
      当前显示 {{ visibleSankey.nodes.length }} / {{ graph.nodes.length }} 个用户、
      {{ visibleSankey.edges.length }} / {{ graph.edges.length }} 条直接关系
    </div>
    <div class="relationship-sankey__viewport">
      <svg
        class="relationship-sankey__svg"
        :style="{ width: `${layout.width}px` }"
        :viewBox="`0 0 ${layout.width} ${layout.height}`"
        role="img"
        :aria-label="`${graph.nodes.length} 个用户、${graph.edges.length} 条直接邀请关系桑基图，当前显示 ${visibleSankey.nodes.length} 个用户、${visibleSankey.edges.length} 条关系`"
        data-test="relationship-sankey"
      >
        <g class="sankey-links">
          <path
            v-for="link in layout.links"
            :key="link.id"
            :d="link.path"
            :style="{ strokeWidth: link.width }"
            :class="`sankey-link sankey-link--${link.status}`"
            data-test="relationship-sankey-link"
          >
            <title>用户 #{{ link.sourceId }} 邀请用户 #{{ link.targetId }}，{{ statusLabel(link.status) }}</title>
          </path>
        </g>

        <g
          v-for="node in layout.nodes"
          :key="node.id"
          class="sankey-node"
          :class="{
            'sankey-node--inviter': node.outgoingCount > 0,
            'sankey-node--expandable': node.hiddenChildCount > 0,
          }"
          :transform="`translate(${node.x} ${node.y})`"
          :role="node.hiddenChildCount > 0 ? 'button' : undefined"
          :tabindex="node.hiddenChildCount > 0 ? 0 : undefined"
          :aria-label="nodeAriaLabel(node)"
          data-test="relationship-sankey-node"
          @click="expandNode(node.id, node.hiddenChildCount)"
          @keydown.enter.prevent="expandNode(node.id, node.hiddenChildCount)"
          @keydown.space.prevent="expandNode(node.id, node.hiddenChildCount)"
        >
          <title>{{ nodeTitle(node) }}</title>
          <rect :width="node.width" :height="node.height" rx="8" />
          <text class="sankey-node__label" x="10" y="18">{{ shortLabel(node.label) }}</text>
          <text
            class="sankey-node__meta"
            x="10"
            y="34"
            role="button"
            tabindex="0"
            :aria-label="`查看用户 ID ${node.id} 详情`"
            @click.stop="emit('selectUser', node.id)"
            @keydown.enter.stop.prevent="emit('selectUser', node.id)"
            @keydown.space.stop.prevent="emit('selectUser', node.id)"
          >
            #{{ node.id }} · 邀请 {{ node.outgoingCount }}
          </text>
          <text
            v-if="node.hiddenChildCount > 0"
            class="sankey-node__expand"
            :x="node.width - 12"
            :y="node.height - 9"
            text-anchor="end"
          >
            展开下一层（{{ node.hiddenChildCount }}）›
          </text>
        </g>
      </svg>
    </div>
  </div>
  <div v-else class="relationship-sankey__empty" data-test="relationship-sankey-empty">
    当前筛选用户没有可绘制的直接邀请关系
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type {
  ReferralGraphEdge,
  ReferralRelationshipAnalysis,
} from '@/types/user-statistics'
import type { ReferralSankeyLayoutNode } from '@/utils/referral-sankey'
import {
  buildVisibleReferralSankey,
  layoutReferralSankey,
} from '@/utils/referral-sankey'
import { buildReferralGraph } from '@/utils/user-statistics'

const props = defineProps<{
  relationships: ReferralRelationshipAnalysis[]
}>()
const emit = defineEmits<{ selectUser: [userId: number] }>()

const expandedNodeIds = ref<Set<number>>(new Set())
const graph = computed(() => buildReferralGraph(props.relationships))
const visibleSankey = computed(() => buildVisibleReferralSankey(graph.value, expandedNodeIds.value))
const layout = computed(() => layoutReferralSankey(visibleSankey.value))

watch(() => props.relationships, () => {
  expandedNodeIds.value = new Set()
})

const expandNode = (id: number, hiddenChildCount: number) => {
  if (!hiddenChildCount || expandedNodeIds.value.has(id)) return
  expandedNodeIds.value = new Set([...expandedNodeIds.value, id])
}

const shortLabel = (label: string) => label.length > 16 ? `${label.slice(0, 15)}…` : label
const statusLabel = (status: ReferralGraphEdge['status']) => ({
  qualified: '已达标',
  pending: '待达标',
  rejected: '已拒绝',
})[status]
const nodeTitle = (node: ReferralSankeyLayoutNode) => (
  `${node.label}（ID ${node.id}），邀请 ${node.outgoingCount} 人`
)
const nodeAriaLabel = (node: ReferralSankeyLayoutNode) => {
  const base = nodeTitle(node)
  return node.hiddenChildCount > 0
    ? `${base}，点击展开 ${node.hiddenChildCount} 个下一层用户`
    : base
}
</script>

<style scoped>
.relationship-sankey {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.relationship-sankey__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem 1rem;
  padding: 0.8rem 1rem;
  color: #475569;
  font-size: 0.76rem;
  border-bottom: 1px solid #e2e8f0;
}

.relationship-sankey__legend span {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.legend-flow {
  width: 1.5rem;
  height: 0.45rem;
  border-radius: 999px;
  opacity: 0.62;
}

.legend-flow--qualified { background: #059669; }
.legend-flow--pending { background: #ca8a04; }
.legend-flow--rejected { background: #e11d48; }

.legend-node {
  width: 0.9rem;
  height: 0.75rem;
  background: #dbeafe;
  border: 2px solid #2563eb;
  border-radius: 3px;
}

.relationship-sankey__summary {
  padding: 0.55rem 1rem;
  color: #64748b;
  font-size: 0.76rem;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.relationship-sankey__viewport {
  max-height: 38rem;
  overflow: auto;
}

.relationship-sankey__svg {
  display: block;
  min-width: 100%;
  height: auto;
  min-height: 22rem;
}

.sankey-link {
  fill: none;
  opacity: 0.42;
  stroke-linecap: butt;
}

.sankey-link--qualified { stroke: #059669; }
.sankey-link--pending { stroke: #ca8a04; }
.sankey-link--rejected { stroke: #e11d48; }

.sankey-node rect {
  fill: #fff;
  stroke: #64748b;
  stroke-width: 2;
}

.sankey-node--inviter rect {
  fill: #eff6ff;
  stroke: #2563eb;
}

.sankey-node--expandable {
  cursor: pointer;
  outline: none;
}

.sankey-node--expandable:hover rect,
.sankey-node--expandable:focus rect {
  fill: #dbeafe;
  stroke-width: 3;
}

.sankey-node__label {
  fill: #0f172a;
  font-size: 12px;
  font-weight: 700;
}

.sankey-node__meta {
  fill: #64748b;
  font-size: 9px;
  cursor: pointer;
  text-decoration: underline;
}

.sankey-node__expand {
  fill: #1d4ed8;
  font-size: 9px;
  font-weight: 700;
}

.relationship-sankey__empty {
  padding: 2.5rem;
  color: #64748b;
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
}
</style>
